import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { countryCodes } from '../../data/countryCodes';

/**
 * CountryCodeSelect Component
 * A searchable dropdown for selecting country codes
 *
 * @param {string} value - Current country code value (with or without +)
 * @param {function} onChange - Callback when country code changes
 * @param {string} className - Additional CSS classes
 * @param {boolean} disabled - Whether the select is disabled
 */
export default function CountryCodeSelect({ value, onChange, className = '', disabled = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Normalize value to include +
  const normalizedValue = value && !value.startsWith('+') ? `+${value}` : value;

  // Find selected country
  const selectedCountry = countryCodes.find(country => country.code === normalizedValue) ||
                          countryCodes.find(country => country.iso === 'IN'); // Default to India

  // Filter countries based on search term
  const filteredCountries = countryCodes.filter(country => {
    const search = searchTerm.toLowerCase();
    return (
      country.name.toLowerCase().includes(search) ||
      country.code.includes(search) ||
      country.iso.toLowerCase().includes(search)
    );
  });

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Focus search input when dropdown opens
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle escape key to close dropdown
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handleSelect = (country) => {
    onChange(country.code);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      {/* Selected Value Display */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full h-10 px-3 py-2
          flex items-center justify-between gap-2
          bg-white border border-gray-300 rounded-md
          text-sm text-left
          transition-all duration-200
          ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'hover:border-gray-400 cursor-pointer'}
          ${isOpen ? 'ring-2 ring-blue-500 border-blue-500' : ''}
        `}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="text-lg flex-shrink-0">{selectedCountry.flag}</span>
          <span className="font-medium flex-shrink-0">{selectedCountry.code}</span>
          <span className="text-gray-500 truncate hidden sm:inline">{selectedCountry.name}</span>
        </div>
        <ChevronDown
          className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 w-full sm:w-80 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 flex flex-col">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search country..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Country List */}
          <div className="overflow-y-auto flex-1">
            {filteredCountries.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500">
                No countries found
              </div>
            ) : (
              filteredCountries.map((country) => (
                <button
                  key={country.iso}
                  type="button"
                  onClick={() => handleSelect(country)}
                  className={`
                    w-full px-4 py-2.5
                    flex items-center gap-3
                    text-sm text-left
                    transition-colors duration-150
                    ${country.code === selectedCountry.code
                      ? 'bg-blue-50 text-blue-700'
                      : 'hover:bg-gray-50 text-gray-900'
                    }
                  `}
                >
                  <span className="text-xl flex-shrink-0">{country.flag}</span>
                  <span className="flex-1 font-medium truncate">{country.name}</span>
                  <span className="text-gray-500 font-mono text-xs flex-shrink-0">{country.code}</span>
                </button>
              ))
            )}
          </div>

          {/* Footer Info */}
          <div className="px-3 py-2 border-t border-gray-200 bg-gray-50">
            <p className="text-xs text-gray-500 text-center">
              {filteredCountries.length} {filteredCountries.length === 1 ? 'country' : 'countries'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
